/** axios封装 **/
import axios from "axios";
import { config } from "./config";
import { Message } from "element-ui";
import { LibMsgBox } from "qlmbidding2_pc_lib";
import { getUUID, LOGOUT_TYPE, redirectToLoginPage } from "@/utils/common";
import Qs from "qs";
import {
  cancelRequest,
  lastRequestStatus,
  requestAdd,
  requestDelete,
  updateLastRequestStatus,
} from "@/utils/axios.canceler";
import { CDN } from "@/utils/constants/constants";
import { USER_CENTER_URL } from "@/utils/constants/constants.menu";
import { default as $router } from "@/router/index";
import { CookieKeys, Cookies } from "./cookieKey";
import { renderCaptcha } from "@/utils/captcha";

// 登出提醒当前是否已显示
let isLogoutToastOnShow = false;

async function handleSuccessResponse(res, options) {
  const { disableErrorMessage } = options;
  updateLastRequestStatus(res.status);
  if (res.status === config.HTTP_CODE.SUCCESS) {
    /** 判断业务code **/
    const code = parseInt(res.data.code);
    switch (code) {
      /** code 为 200 **/
      case config.STATUS_CODE.SUCCESS:
        // x-auth-token从响应头取值，注入cookie
        if (res.headers["x-auth-token"])
          Cookies.set(CookieKeys.xAuthToken, res.headers["x-auth-token"], { domain: ".qianlima.com" });
        return Promise.resolve(res.data);
      /** code 为 310300 **/
      case config.STATUS_CODE.PERMISSION_EXPIRATION:
        LibMsgBox({
          title: "提示",
          message: res.data.msg || "权限过期",
          type: "error",
          showCancelButton: false,
          confirmButtonText: "返回首页",
        }).then(() => {
          $router.replace({
            path: USER_CENTER_URL,
          });
        });
        return Promise.reject(res.data);
      /** code 为 503 **/
      case config.STATUS_CODE.SERVER_ERR:
        if (!disableErrorMessage) {
          Message.error(`网络错误，请稍后重试[SERVER_ERR ${code}]`);
        }
        return Promise.reject(res.data);
      default:
        /**
         * res.data 为
         * {
         *  code: [CODE],
         *  data: [DATA],
         *  msg: [msg]
         * }
         * **/
        return Promise.reject(res.data);
    }
  } else {
    return Promise.reject(res.data);
  }
}

async function handleErrorResponse(response, options) {
  const httpCode = response && response.status;
  const { disableErrorMessage, requestOptionsForRepeat } = options;
  if (httpCode) updateLastRequestStatus(httpCode);
  const data = (response && response.data) || {};
  switch (httpCode) {
    case config.HTTP_CODE.UNAUTHORIZED:
      /** 401 **/
      if (isLogoutToastOnShow) return;
      isLogoutToastOnShow = true;
      LibMsgBox({
        title: "提示",
        message: "登录状态超时，请重新登录!",
        type: "error",
        showCancelButton: false,
      }).then(() => {
        isLogoutToastOnShow = false;
        redirectToLoginPage(LOGOUT_TYPE.AUTO);
      });
      return Promise.reject(data);
    case config.HTTP_CODE.PAYMENT_REQUIRED:
      /** 402 **/
      if (isLogoutToastOnShow) return;
      isLogoutToastOnShow = true;
      Message({
        message: "账号已在其他设备登录，请重新登录",
        type: "info",
        onClose: () => {
          isLogoutToastOnShow = false;
          redirectToLoginPage(LOGOUT_TYPE.AUTO);
        },
      });
      return Promise.reject(data);
    case config.HTTP_CODE.NO_AUTHORISE:
      /** 403 **/
      Message({
        message: "当前账号没有访问权限！",
        type: "info",
      });
      return Promise.reject(data);
    case config.HTTP_CODE.REDO_LOGIN:
      /** 406 **/
      Message({
        message: "请重新登录!",
        type: "info",
        onClose: () => {
          redirectToLoginPage(LOGOUT_TYPE.AUTO);
        },
      });
      return Promise.reject(data);

    case config.HTTP_CODE.TOO_FREQUENTLY_429: //滑块验证
      Cookies.remove(CookieKeys.accessCaptchaPermission, { domain: ".qianlima.com" });
      console.warn("headers[Access-Captcha-Permission] is expired or missing, 需要执行滑块安全验证");
      const res = await renderCaptcha({ withCredentials: false });
      const { captchaVerification } = res;
      Cookies.set(CookieKeys.accessCaptchaPermission, captchaVerification || "", { domain: ".qianlima.com" });
      return omniAxios(requestOptionsForRepeat);
    case config.HTTP_CODE.TOO_FREQUENTLY_521:
      /** 429 521 **/
      if (data.msg) {
        if (!disableErrorMessage) {
          Message({
            message: data.msg,
            type: "info",
          });
        }
      }
      return Promise.reject(response);
    default: // canceler
      /** 不满足所有case时，直接返回response本身 **/
      if (response.message && response.message.includes("Axios Canceler")) {
        console.warn(response.message);
        response = {
          ...response,
          status: config.STATUS_CODE.REQUEST_CANCEL,
        };
      }
      return Promise.reject(response);
  }
}

function getHeaders(isFormData) {
  return {
    "X-Auth-Token": Cookies.get(CookieKeys.xAuthToken) || "None",
    "Content-Type": isFormData ? "application/x-www-form-urlencoded" : "application/json",
    "Access-Captcha-Permission": Cookies.get(CookieKeys.accessCaptchaPermission) || "None",
  };
}

/**
 * Axios二次封装
 * @param url 请求路径
 * @param method 请求方法
 * @param params 请求参数
 * @param isTracker 是否为埋点接口
 * @param isFromOss 是否为OSS接口
 * @param withCredentials 是否携带身份认证
 * @param isFormData 入参是否为form表单
 * @param useDevServerProxy 开发阶段是否使用反向代理
 * @param cancelLastSameRequest 当浏览器中存在相同url路径且在pending中的请求时，是否需要中断上一个
 * @param disableErrorMessage 禁止错误message弹出，如高频且不影响流程的埋点接口
 * @param header 特殊接口需要单独添加的header信息
 * @param timeout 超时时间，默认无超时时间，毫秒
 * @param isOnePointZeroApi 1.0Api，baseUrl不同于常规2.0接口
 * @returns {Promise<* | undefined>}
 */
export function omniAxios({
  url,
  method = "get",
  params = {},
  header = {},
  isTracker = false,
  isFromOss = false,
  withCredentials = true,
  isFormData = false,
  useDevServerProxy = false,
  cancelLastSameRequest = false,
  disableErrorMessage = false,
  timeout = 0, // 默认无超时时间
  isOnePointZeroApi = false,
}) {
  const requestId = getUUID();
  let baseURL;
  if (url.includes("http")) {
    baseURL = "";
  } else {
    if (isOnePointZeroApi) {
      baseURL = process.env[`VUE_APP_1.0_API`];
    } else if (isTracker) {
      /** 是否使用反向代理的判断 **/
      if (process.env.NODE_ENV === "development" && useDevServerProxy) {
        baseURL = "/proxyApiTrack";
      } else {
        baseURL = process.env[`VUE_APP_API_TRACK`];
      }
    } else if (isFromOss) {
      baseURL = CDN;
    } else {
      /** 是否使用反向代理的判断 **/
      if (process.env.NODE_ENV === "development" && useDevServerProxy) {
        baseURL = "/proxyApi";
      } else {
        baseURL = process.env[`VUE_APP_API`];
      }
    }
  }
  const axiosOptions = {
    baseURL,
    url,
    method,
    withCredentials, // 确保请求携带cookie
    headers: {
      ...header,
      ...getHeaders(isFormData),
    },
    cancelToken: new axios.CancelToken(function (canceler) {
      /** 如上次请求回调为401，则取消本次请求 **/
      if (lastRequestStatus === config.HTTP_CODE.UNAUTHORIZED) {
        cancelRequest({
          canceler,
          requestId,
          path: url,
          reason: "UNAUTHORIZED",
        });
      }
      /** 中断pending中的相同请求 **/
      if (cancelLastSameRequest) {
        cancelRequest({ path: url, reason: "Same request is pending" });
      }
      requestAdd({
        requestId,
        path: url,
        canceler,
      });
    }),
    timeout,
  };
  switch (method.toLowerCase()) {
    case "get":
      axiosOptions.params = params;
      break;
    case "post":
      axiosOptions.data = isFormData ? Qs.stringify(params) : params;
      break;
  }
  return axios(axiosOptions)
    .then((res) => {
      requestDelete(requestId);
      return Promise.resolve(res);
    })
    .then((res) => {
      return handleSuccessResponse(res, { disableErrorMessage });
    })
    .catch((e) => {
      /**
       * handleSuccessResponse reject出来的东西
       * 会首先进入这里
       * 所以抛出来的东西不存在response时
       * 可以直接透传e本身
       * **/
      const requestOptionsForRepeat = {
        url,
        method,
        params,
        header,
        isTracker,
        isFromOss,
        withCredentials,
        isFormData,
        useDevServerProxy,
        cancelLastSameRequest,
        disableErrorMessage,
        timeout,
      };
      return handleErrorResponse(e.response || e, { requestOptionsForRepeat, disableErrorMessage });
    });
}
